/**
 * Optimized Database Queries
 *
 * Performance-optimized queries with:
 * - N+1 query prevention using batch loading
 * - Server-side cursor pagination
 * - Efficient joins and data fetching
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { normalizeStatus, isCompletedStatus, TASK_STATUS } from '@/constants/taskStatuses';
import type { Task, TaskDoc } from './index';

// Helper to convert Supabase task (snake_case) to UI format (camelCase)
function convertTaskFromSupabase(row: TaskDoc): Task {
  return {
    id: row.id,
    name: row.name,
    content: row.content ?? '',
    status: normalizeStatus(row.status),
    dateCreated: row.date_created ?? Date.now(),
    dueDate: row.due_date ?? null,
    startDate: row.start_date ?? null,
    parentId: row.parent_id ?? null,
    spaceId: row.space_id,
    sprintId: row.sprint_id ?? null,
    epicId: row.epic_id ?? null,
    milestoneId: row.milestone_id ?? null,
    templateId: row.template_id ?? null,
    storyPoints: row.story_points ?? null,
    attachments: row.attachments || [],
    assignees: row.assignees || [],
    tags: row.tags || [],
    priority: row.priority ?? null,
    timeEstimated: row.time_estimated ?? null,
    timeEstimatesByAssignee: row.time_estimates_by_assignee ?? null,
    rolledUpTime: row.rolled_up_time ?? null,
    checklists: row.checklists || {},
    comments: row.comments || [],
    assignedComments: row.assigned_comments ?? 0,
    customFields: row.custom_fields ?? null,
    estimateHistory: row.estimate_history ?? null,
    baselineStartDate: row.baseline_start_date ?? null,
    baselineDueDate: row.baseline_due_date ?? null,
    scheduledStart: row.scheduled_start ?? null,
    scheduledEnd: row.scheduled_end ?? null,
    sortOrder: row.sort_order ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Pagination result type
 */
export interface PaginatedResult<T> {
  data: T[];
  nextCursor: string | null;
  hasMore: boolean;
  totalCount?: number;
}

/**
 * Fetch tasks with cursor-based pagination
 * More efficient than offset pagination for large datasets
 */
export async function fetchTasksPage(options: {
  cursor?: string | null;
  limit?: number;
  spaceId?: string;
  status?: string[];
  assignees?: string[];
  orderBy?: 'created_at' | 'due_date' | 'updated_at';
  orderDirection?: 'asc' | 'desc';
}): Promise<PaginatedResult<Task>> {
  const {
    cursor = null,
    limit = 100,
    spaceId,
    status,
    assignees,
    orderBy = 'created_at',
    orderDirection = 'desc',
  } = options;

  const { data, error, count } = await withRetry(() => {
    let query = supabase
      .from('tasks')
      .select('*', { count: 'exact' })
      .order(orderBy, { ascending: orderDirection === 'asc' })
      .limit(limit + 1); // Fetch one extra to check if there are more

    // Apply cursor-based pagination
    if (cursor) {
      if (orderDirection === 'desc') {
        query = query.lt(orderBy, cursor);
      } else {
        query = query.gt(orderBy, cursor);
      }
    }

    // Apply filters
    if (spaceId) {
      query = query.eq('space_id', spaceId);
    }

    if (status && status.length > 0) {
      query = query.in('status', status);
    }

    if (assignees && assignees.length > 0) {
      query = query.overlaps('assignees', assignees);
    }

    return query;
  });

  if (error) throw error;

  const hasMore = (data?.length || 0) > limit;
  const tasks = (data || []).slice(0, limit).map(convertTaskFromSupabase);
  const lastTask = tasks.length > 0 ? tasks[tasks.length - 1] : null;
  let nextCursor: string | null = null;
  if (hasMore && lastTask) {
    const cursorField = orderBy === 'created_at' ? 'createdAt' : orderBy === 'due_date' ? 'dueDate' : 'updatedAt';
    const rawValue = lastTask[cursorField];
    // Preserve numeric values as-is (timestamps are BIGINT in the DB);
    // ISO strings pass through unchanged.
    nextCursor = typeof rawValue === 'number' ? rawValue.toString(10) : String(rawValue ?? '');
  }

  return {
    data: tasks,
    nextCursor,
    hasMore,
    totalCount: count ?? undefined,
  };
}

/**
 * Batch load spaces/containers by IDs
 * Prevents N+1 when loading container info for multiple tasks
 */
export async function batchLoadSpaces(spaceIds: string[]): Promise<Map<string, {
  id: string;
  name: string;
  type: string;
  parentId: string | null;
}>> {
  if (spaceIds.length === 0) return new Map();

  // Deduplicate IDs
  const uniqueIds = [...new Set(spaceIds)];

  const { data, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id, name, type, parent_id')
      .in('id', uniqueIds)
  );

  if (error) throw error;

  const map = new Map<string, { id: string; name: string; type: string; parentId: string | null }>();
  (data || []).forEach(space => {
    map.set(space.id, {
      id: space.id,
      name: space.name,
      type: space.type,
      parentId: space.parent_id,
    });
  });

  return map;
}

/**
 * Batch load task relations
 * Prevents N+1 when loading dependencies for multiple tasks
 */
export async function batchLoadRelations(taskIds: string[]): Promise<Map<string, Array<{
  id: string;
  sourceTaskId: string;
  targetTaskId: string;
  relationType: string;
}>>> {
  if (taskIds.length === 0) return new Map();

  const uniqueIds = [...new Set(taskIds)];

  const { data, error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .select('id, source_task_id, target_task_id, relation_type')
      .or(`source_task_id.in.(${uniqueIds.join(',')}),target_task_id.in.(${uniqueIds.join(',')})`)
  );

  if (error) throw error;

  const map = new Map<string, Array<{
    id: string;
    sourceTaskId: string;
    targetTaskId: string;
    relationType: string;
  }>>();

  (data || []).forEach(rel => {
    const relation = {
      id: rel.id,
      sourceTaskId: rel.source_task_id,
      targetTaskId: rel.target_task_id,
      relationType: rel.relation_type,
    };

    // Add to source task's relations
    const sourceRels = map.get(rel.source_task_id) || [];
    sourceRels.push(relation);
    map.set(rel.source_task_id, sourceRels);

    // Add to target task's relations
    const targetRels = map.get(rel.target_task_id) || [];
    targetRels.push(relation);
    map.set(rel.target_task_id, targetRels);
  });

  return map;
}

/**
 * Optimized query to get all folders for multiple projects in one query
 * Fixes N+1: Instead of querying folders per project
 */
export async function batchLoadFoldersByProjects(projectIds: string[]): Promise<Map<string, Array<{
  id: string;
  name: string;
  orderIndex: number;
}>>> {
  if (projectIds.length === 0) return new Map();

  const { data, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id, name, parent_id, order_index')
      .eq('type', 'folder')
      .in('parent_id', projectIds)
      .order('order_index', { ascending: true })
      .order('name', { ascending: true })
  );

  if (error) throw error;

  const map = new Map<string, Array<{ id: string; name: string; orderIndex: number }>>();

  // Initialize empty arrays for all project IDs
  projectIds.forEach(id => map.set(id, []));

  // Group folders by parent project
  (data || []).forEach(folder => {
    const projectFolders = map.get(folder.parent_id) || [];
    projectFolders.push({
      id: folder.id,
      name: folder.name,
      orderIndex: folder.order_index || 0,
    });
    map.set(folder.parent_id, projectFolders);
  });

  return map;
}

/**
 * Optimized query to get all lists for multiple containers in one query
 */
export async function batchLoadListsByContainers(containerIds: string[]): Promise<Map<string, Array<{
  id: string;
  name: string;
  orderIndex: number;
}>>> {
  if (containerIds.length === 0) return new Map();

  const { data, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id, name, parent_id, order_index')
      .eq('type', 'list')
      .in('parent_id', containerIds)
      .order('order_index', { ascending: true })
      .order('name', { ascending: true })
  );

  if (error) throw error;

  const map = new Map<string, Array<{ id: string; name: string; orderIndex: number }>>();

  // Initialize empty arrays for all container IDs
  containerIds.forEach(id => map.set(id, []));

  // Group lists by parent container
  (data || []).forEach(list => {
    const containerLists = map.get(list.parent_id) || [];
    containerLists.push({
      id: list.id,
      name: list.name,
      orderIndex: list.order_index || 0,
    });
    map.set(list.parent_id, containerLists);
  });

  return map;
}

/**
 * Load full project hierarchy in one optimized query
 * Returns projects with their folders and lists
 */
export async function loadProjectHierarchy(): Promise<{
  projects: Array<{ id: string; name: string; description?: string }>;
  folders: Map<string, Array<{ id: string; name: string; orderIndex: number }>>;
  lists: Map<string, Array<{ id: string; name: string; orderIndex: number }>>;
}> {
  // Single query to get all spaces
  const { data: allSpaces, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id, name, description, type, parent_id, order_index')
      .order('order_index', { ascending: true })
      .order('name', { ascending: true })
  );

  if (error) throw error;

  const spaces = allSpaces || [];

  // Extract projects (root level)
  const projects = spaces
    .filter(s => s.type === 'project' && !s.parent_id)
    .map(p => ({
      id: p.id,
      name: p.name,
      description: p.description || undefined,
    }));

  // Group folders by project
  const folders = new Map<string, Array<{ id: string; name: string; orderIndex: number }>>();
  spaces
    .filter(s => s.type === 'folder')
    .forEach(folder => {
      const projectFolders = folders.get(folder.parent_id) || [];
      projectFolders.push({
        id: folder.id,
        name: folder.name,
        orderIndex: folder.order_index || 0,
      });
      folders.set(folder.parent_id, projectFolders);
    });

  // Group lists by parent (folder or project)
  const lists = new Map<string, Array<{ id: string; name: string; orderIndex: number }>>();
  spaces
    .filter(s => s.type === 'list')
    .forEach(list => {
      const parentLists = lists.get(list.parent_id) || [];
      parentLists.push({
        id: list.id,
        name: list.name,
        orderIndex: list.order_index || 0,
      });
      lists.set(list.parent_id, parentLists);
    });

  return { projects, folders, lists };
}

/**
 * Optimized task count by container
 * Returns task counts for multiple containers in one query
 */
export async function getTaskCountsByContainers(containerIds: string[]): Promise<Map<string, number>> {
  if (containerIds.length === 0) return new Map();

  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('space_id')
      .in('space_id', containerIds)
  );

  if (error) throw error;

  const counts = new Map<string, number>();
  containerIds.forEach(id => counts.set(id, 0));

  (data || []).forEach(task => {
    const count = counts.get(task.space_id) || 0;
    counts.set(task.space_id, count + 1);
  });

  return counts;
}

/**
 * Search tasks with PostgreSQL full-text search (ts_vector + GIN index).
 * Falls back to ILIKE if the RPC is unavailable.
 */
export async function searchTasks(options: {
  query: string;
  spaceId?: string;
  limit?: number;
}): Promise<Task[]> {
  const { query, spaceId, limit = 30 } = options;

  if (!query.trim()) return [];

  // Try full-text search via RPC
  const { data, error } = await supabase.rpc('search_tasks_fts', {
    search_query: query.trim(),
    p_space_id: spaceId || null,
    p_limit: limit,
  });

  if (!error && data) {
    return (data as TaskDoc[]).map(convertTaskFromSupabase);
  }

  // Fallback: ILIKE search
  const { data: fallbackData, error: fallbackError } = await withRetry(() => {
    let dbQuery = supabase
      .from('tasks')
      .select('*')
      .or(`name.ilike.%${query}%,content.ilike.%${query}%`)
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (spaceId) {
      dbQuery = dbQuery.eq('space_id', spaceId);
    }

    return dbQuery;
  });

  if (fallbackError) throw fallbackError;

  return (fallbackData || []).map(convertTaskFromSupabase);
}

/**
 * Get tasks due soon (upcoming deadlines)
 * Optimized query for dashboard/notifications
 */
export async function getTasksDueSoon(options: {
  days?: number;
  assignee?: string;
  limit?: number;
}): Promise<Task[]> {
  const { days = 7, assignee, limit = 50 } = options;

  const now = Date.now();
  const futureDate = now + days * 24 * 60 * 60 * 1000;

  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('tasks')
      .select('*')
      .gte('due_date', now)
      .lte('due_date', futureDate)
      .not('status', 'in', '("completed","closed","cancelled")')
      .order('due_date', { ascending: true })
      .limit(limit);

    if (assignee) {
      query = query.contains('assignees', [assignee]);
    }

    return query;
  });

  if (error) throw error;

  return (data || []).map(convertTaskFromSupabase);
}

/**
 * Get overdue tasks
 */
export async function getOverdueTasks(options: {
  assignee?: string;
  limit?: number;
}): Promise<Task[]> {
  const { assignee, limit = 50 } = options;

  const now = Date.now();

  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('tasks')
      .select('*')
      .lt('due_date', now)
      .not('status', 'in', '("completed","closed","cancelled")')
      .order('due_date', { ascending: true })
      .limit(limit);

    if (assignee) {
      query = query.contains('assignees', [assignee]);
    }

    return query;
  });

  if (error) throw error;

  return (data || []).map(convertTaskFromSupabase);
}

/**
 * Aggregate task statistics
 * Single query for dashboard stats
 */
export async function getTaskStats(spaceId?: string): Promise<{
  total: number;
  completed: number;
  inProgress: number;
  blocked: number;
  overdue: number;
}> {
  const { data, error } = await withRetry(() => {
    let query = supabase.from('tasks').select('status, due_date');

    if (spaceId) {
      query = query.eq('space_id', spaceId);
    }

    return query;
  });

  if (error) throw error;

  const now = Date.now();
  const tasks = data || [];

  const stats = {
    total: tasks.length,
    completed: 0,
    inProgress: 0,
    blocked: 0,
    overdue: 0,
  };

  tasks.forEach(task => {
    const status = normalizeStatus(task.status);

    if (isCompletedStatus(status)) {
      stats.completed++;
    } else if (status === TASK_STATUS.IN_PROGRESS) {
      stats.inProgress++;
    } else if (status === TASK_STATUS.BLOCKED) {
      stats.blocked++;
    }

    // Count overdue (not completed and past due date)
    if (
      task.due_date &&
      task.due_date < now &&
      !isCompletedStatus(status)
    ) {
      stats.overdue++;
    }
  });

  return stats;
}
